import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { Collection, Resource } from '../../lib/types';
import {
  appSelectors,
  clearAddCollectionResourcesLoading,
  clearSelected,
  deleteFromSelected,
} from '../../redux/appSlice';
import CollectionSelect from '../CollectionSelect';
import Modal from '../Modal';
import DownloadAll from '../DownloadAll';

export const SelectionBar = () => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const loggedIn: boolean = useAppSelector(appSelectors.isLoggedIn);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const selectedResources: Resource[] = useAppSelector(
    appSelectors.selectedResources
  );
  const userCollections: Collection[] = useAppSelector(
    appSelectors.userCollections
  );

  const [showAddResourcesModal, setShowAddResourcesModal] =
    useState<boolean>(false);

  const [showDownloadAllModal, setDownloadAllModal] = useState<boolean>(false);

  const handleOpenClick = () => {
    setIsOpen((currentOpen) => !currentOpen);
  };

  const handleRemoveResource = useCallback(
    (resource: Resource) => {
      dispatch(deleteFromSelected(resource));
    },
    [dispatch]
  );

  const handleClearCollectionsClick = useCallback(() => {
    dispatch(clearSelected());
  }, [dispatch]);

  const handleAddToCollectionClick = useCallback(() => {
    if (selectedResources) {
      setShowAddResourcesModal(true);
    }
  }, [selectedResources]);

  const handleDownloadClick = useCallback(async () => {
    setDownloadAllModal(true);
  }, []);

  const handleAddResourceToCollectionClose = useCallback(
    (closeSelectionBar: boolean = false) => {
      setShowAddResourcesModal(false);
      if (closeSelectionBar) {
        setIsOpen(false);
      }
      dispatch(clearAddCollectionResourcesLoading());
    },
    [dispatch]
  );

  const handleDownloadAllClose = useCallback(() => {
    setDownloadAllModal(false);
  }, []);

  useEffect(() => {
    if (wrapperRef.current) {
      const handleScroll = () => {
        const wrapper = wrapperRef.current as HTMLDivElement;
        const rect = wrapper.getBoundingClientRect();

        const windowHeight = document.documentElement.clientHeight;
        // console.log(windowHeight, Math.round(rect.top));
        const isStuck = Math.round(rect.top) >= windowHeight;

        if (isStuck) {
          wrapper.classList.add('stuck');
        } else {
          wrapper.classList.remove('stuck');
        }
      };

      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);

      const observer = new ResizeObserver((entries) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        for (const _ of entries) {
          handleScroll();
        }
      });
      observer.observe(document.querySelector('body') as Element);

      return () => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
        observer.disconnect();
      };
    }
  }, [wrapperRef]);

  const wrapperClasses = classNames('selection-bar', {
    open: isOpen,
    closed: !isOpen,
  });

  const selectedResourcesData = selectedResources.map((resource: Resource) => {
    return {
      ref: resource.ref,
      file_extension: resource.file_extension,
      preview_extension: resource.preview_extension,
    };
  });

  return (
    <div className={wrapperClasses} ref={wrapperRef}>
      <Modal
        showBack={true}
        title="Add your selected images to a collection"
        isOpened={showAddResourcesModal}
        onClose={handleAddResourceToCollectionClose}
      >
        <CollectionSelect
          close={() => handleAddResourceToCollectionClose(true)}
        />
      </Modal>
      <Modal
        showBack={true}
        title="Download multiple files"
        isOpened={showDownloadAllModal}
        onClose={handleDownloadAllClose}
      >
        <DownloadAll resourceData={selectedResourcesData} />
      </Modal>
      <div className="selection-bar-inner">
        <h2>
          <button onClick={handleOpenClick}>
            {`Your Selections (${selectedResources.length})`}
          </button>
        </h2>
        <div className="selected-image-wrapper">
          <ul>
            {selectedResources.map((resource: Resource, index: number) => (
              <li key={`selected-resource-${resource.ref}-${index}`}>
                <img src={resource.url_mth} />
                <button onClick={() => handleRemoveResource(resource)} />
              </li>
            ))}
          </ul>
          <div className="selected-image-actions">
            {loggedIn && (
              <button
                disabled={
                  userCollections?.length === 0 ||
                  selectedResources?.length === 0
                }
                onClick={handleAddToCollectionClick}
              >
                Add to collection
              </button>
            )}
            {selectedResources && selectedResources.length > 0 && (
              <>
                <button
                  disabled={selectedResources.length <= 0}
                  onClick={handleDownloadClick}
                >
                  Download all
                </button>
                <button
                  disabled={selectedResources?.length === 0}
                  onClick={handleClearCollectionsClick}
                >
                  Clear selection
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
